import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  sraControllerGetVehicleDetailsQueryOptions,
  sraControllerGetVersionInformationFromBrandAndModelQueryOptions,
} from 'korint-sdk';
import { GaugeIcon, UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchInput } from './search-input';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type VehicleVersionFormFieldProps = {
  watchedFieldNames?: Partial<
    Record<
      | 'version'
      | 'brand'
      | 'model'
      | 'fiscalPower'
      | 'numberOfSeats'
      | 'energyCode'
      | 'firstCirculationDate'
      | 'newValue',
      string
    >
  >;
};

export const vehicleVersionDefaultWatchedFieldNames = {
  version: 'version',
  brand: 'brand',
  model: 'model',
  fiscalPower: 'fiscalPower',
  numberOfSeats: 'numberOfSeats',
  energyCode: 'energyCode',
  firstCirculationDate: 'firstCirculationDate',
  newValue: 'vehicleNewValue',
};

function getWatchedFieldName(
  propertyName: keyof typeof vehicleVersionDefaultWatchedFieldNames,
  watchedFields: Partial<
    Record<
      | 'version'
      | 'brand'
      | 'model'
      | 'fiscalPower'
      | 'numberOfSeats'
      | 'energyCode'
      | 'firstCirculationDate'
      | 'newValue',
      string
    >
  >,
) {
  if (watchedFields[propertyName]) {
    return watchedFields[propertyName];
  }
  return vehicleVersionDefaultWatchedFieldNames[propertyName];
}

function formatCommercialisationDate(date: string, locale: string): string {
  return dayjs(date).toDate().toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const COMMERCIALISATION_TOLERANCE_IN_YEARS = 5;

export function VehicleVersionFormField({
  watchedFieldNames = vehicleVersionDefaultWatchedFieldNames,
}: VehicleVersionFormFieldProps) {
  const form = useFormContext();
  const { t, i18n } = useTranslation();

  const [searchString, setSearchString] = useState('');

  const versionFieldName = getWatchedFieldName('version', watchedFieldNames);
  const fiscalPowerFieldName = getWatchedFieldName('fiscalPower', watchedFieldNames);
  const numberOfSeatsFieldName = getWatchedFieldName('numberOfSeats', watchedFieldNames);
  const newValueFieldName = getWatchedFieldName('newValue', watchedFieldNames);

  const [version, brand, model, entryIntoCirculationDate] = form.watch([
    versionFieldName,
    getWatchedFieldName('brand', watchedFieldNames),
    getWatchedFieldName('model', watchedFieldNames),
    getWatchedFieldName('firstCirculationDate', watchedFieldNames),
  ]) as [string | undefined, string | undefined, string | undefined, string | undefined];

  const { data: versions, isLoading } = useQuery({
    ...sraControllerGetVersionInformationFromBrandAndModelQueryOptions({
      brand: brand!,
      model: model!,
      entryIntoCirculationDate,
      commercialisationToleranceInYears: COMMERCIALISATION_TOLERANCE_IN_YEARS,
    }),
    enabled: !!brand && !!model,
    select: data => data.data.map((info, index) => ({ ...info, itemKey: index })),
  });

  const [fiscalPower, numberOfSeats] = form.watch([
    fiscalPowerFieldName,
    numberOfSeatsFieldName,
  ]) as [number | null | undefined, number | null | undefined];

  // Same lookup and row as the backend SRA integration, so the form holds the value it will store
  const { data: pickedVehicle } = useQuery({
    ...sraControllerGetVehicleDetailsQueryOptions({
      brand: brand!,
      model: model!,
      version: version!,
      fiscalPower: fiscalPower ?? undefined,
      numberOfSeats: numberOfSeats ?? undefined,
    }),
    enabled: !!brand && !!model && !!version,
    select: ({ data }) => data[0] ?? null,
  });

  useEffect(() => {
    if (pickedVehicle === undefined) return;
    form.setValue(newValueFieldName, pickedVehicle?.originPriceInEuro ?? null);
  }, [pickedVehicle, newValueFieldName]);

  const displayedVersions = (versions ?? []).filter(({ version: v }) =>
    v?.includes((searchString ?? '').toUpperCase()),
  );

  // A first-circulation date earlier than the chosen version's commercialisation drops it from the
  // filtered list — clear the now-invalid selection (and its derived specs) so it isn't kept.
  useEffect(() => {
    if (isLoading || !version || versions === undefined) return;
    if (!versions.some(info => info.version === version)) {
      form.setValue(versionFieldName, '', { shouldDirty: true, shouldValidate: true });
      form.setValue(fiscalPowerFieldName, null, { shouldDirty: true, shouldValidate: true });
      form.setValue(numberOfSeatsFieldName, null, { shouldDirty: true, shouldValidate: true });
    }
  }, [version, versions, isLoading]);

  // A date is set but no version was commercialised near it: tell the broker rather than
  // showing an empty picker.
  const noVersionNearDate =
    Boolean(entryIntoCirculationDate) &&
    !isLoading &&
    versions !== undefined &&
    versions.length === 0;

  const selectedVersion = versions?.find(info => version === info.version) ?? null;

  return (
    <>
      <SearchInput
        items={displayedVersions}
        disabled={!brand || !model}
        isLoading={isLoading}
        itemKey="itemKey"
        onSelect={({ version, fiscalPower, numberOfSeats }) => {
          // shouldValidate clears the "required" error: the version field is a custom control, not a
          // native input, so setValue alone never re-runs its validation.
          const options = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
          form.setValue(versionFieldName, version, options);
          form.setValue(fiscalPowerFieldName, fiscalPower, options);
          form.setValue(numberOfSeatsFieldName, numberOfSeats, options);
          setSearchString('');
        }}
        placeholder="Rechercher une version"
        renderItem={({ version, fiscalPower, numberOfSeats, commercialisationStartDate }) => (
          <Group align="stretch" justify="between" gap={0} className="w-full">
            <Group align="stretch" gap={0} className="font-medium">
              {version}
            </Group>
            <Group align="stretch">
              {commercialisationStartDate ? (
                <Group gap={0} className="text-xs text-gray-600">
                  Commercialisé le{' '}
                  {formatCommercialisationDate(commercialisationStartDate, i18n.language)}
                </Group>
              ) : null}
              {fiscalPower ? (
                <Group align="stretch" gap={0}>
                  <GaugeIcon className="h-4 w-4 mr-1" />
                  {fiscalPower}
                </Group>
              ) : null}
              {numberOfSeats ? (
                <Group align="stretch" gap={0}>
                  <UsersIcon className="h-4 w-4 mr-1" />
                  {numberOfSeats}
                </Group>
              ) : null}
            </Group>
          </Group>
        )}
        search={searchString}
        selected={selectedVersion}
        renderSelected={({ version }) => <div className="font-medium">{version}</div>}
        setSearch={version => {
          setSearchString(version);
        }}
      />
      {selectedVersion && (
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border bg-muted/40 p-3 text-sm sm:grid-cols-3">
          {selectedVersion.commercialisationStartDate ? (
            <Stack gap={0} className="gap-0.5">
              <dt className="text-xs text-muted-foreground">
                {t('vehicleVersion.commercialisedOn')}
              </dt>
              <dd className="font-medium">
                {formatCommercialisationDate(
                  selectedVersion.commercialisationStartDate,
                  i18n.language,
                )}
              </dd>
            </Stack>
          ) : null}
          {selectedVersion.fiscalPower ? (
            <Stack gap={0} className="gap-0.5">
              <dt className="flex items-center gap-1 text-xs text-muted-foreground">
                <GaugeIcon className="h-3 w-3" />
                {t('fields.fiscalPower.label')}
              </dt>
              <dd className="font-medium">{selectedVersion.fiscalPower}</dd>
            </Stack>
          ) : null}
          {selectedVersion.numberOfSeats ? (
            <Stack gap={0} className="gap-0.5">
              <dt className="flex items-center gap-1 text-xs text-muted-foreground">
                <UsersIcon className="h-3 w-3" />
                {t('fields.numberOfSeats.label')}
              </dt>
              <dd className="font-medium">{selectedVersion.numberOfSeats}</dd>
            </Stack>
          ) : null}
        </dl>
      )}
      {noVersionNearDate && (
        <Text size="sm" className="mt-2 text-amber-700">
          {t('vehicleVersion.noVersionNearDate')}
        </Text>
      )}
    </>
  );
}
